import api from './api';
import torneoService from './torneoService';

/**
 * Servicio para gestión de sedes y geolocalización
 */
class SedeService {
  /**
   * Obtiene todas las sedes
   * @param {Object} filtros - Filtros opcionales
   * @returns {Promise<Object>} Respuesta con sedes
   */
  async getSedes(filtros = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filtros.activo !== undefined) {
        params.append('activo', filtros.activo);
      }
      if (filtros.conCoordenadas) {
        params.append('conCoordenadas', 'true');
      }
      if (filtros.poblacion) {
        params.append('poblacion', filtros.poblacion);
      }

      const response = await api.get(`/postgres/sedes?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener sedes:', error);
      throw error;
    }
  }

  /**
   * Obtiene sedes cercanas a una ubicación
   * @param {number} lat - Latitud
   * @param {number} lng - Longitud
   * @param {number} radio - Radio en kilómetros (default: 50)
   * @returns {Promise<Object>} Respuesta con sedes cercanas
   */
  async getSedesCercanas(lat, lng, radio = 50) {
    try {
      const response = await api.get(`/postgres/sedes/cercanas`, {
        params: { lat, lng, radio }
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener sedes cercanas:', error);
      throw error;
    }
  }

  /**
   * Obtiene sedes con coordenadas configuradas
   * @returns {Promise<Object>} Respuesta con sedes que tienen coordenadas
   */
  async getSedesConCoordenadas() {
    try {
      const response = await api.get(`/postgres/sedes/con-coordenadas`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener sedes con coordenadas:', error);
      throw error;
    }
  }

  /**
   * Obtiene una sede por ID
   * @param {number} id - ID de la sede
   * @returns {Promise<Object>} Respuesta con la sede
   */
  async getSedeById(id) {
    try {
      const response = await api.get(`/postgres/sedes/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener sede:', error);
      throw error;
    }
  }

  /**
   * Obtiene coordenadas aproximadas de una ciudad
   * @param {string} ciudad - Nombre de la ciudad
   * @returns {Object|null} Coordenadas {lat, lng} o null
   */
  getCoordenadasCiudad(ciudad) {
    const ciudadesEspana = {
      // Comunidad Valenciana
      'alicante': { lat: 38.3452, lng: -0.4810 },
      'valencia': { lat: 39.4699, lng: -0.3763 },
      'castellon': { lat: 39.9864, lng: -0.0513 },
      'elche': { lat: 38.2622, lng: -0.7011 },
      'torrevieja': { lat: 37.9787, lng: -0.6814 },
      'orihuela': { lat: 38.0844, lng: -0.9481 },
      'algorfa': { lat: 38.0829, lng: -0.7963 },
      'almoradi': { lat: 38.1167, lng: -0.7833 },
      'almoradí': { lat: 38.1167, lng: -0.7833 }, // Con tilde
      'guardamar': { lat: 38.0919, lng: -0.6556 },
      'santa pola': { lat: 38.1919, lng: -0.5656 },
      
      // Madrid
      'madrid': { lat: 40.4168, lng: -3.7038 },
      
      // Cataluña
      'barcelona': { lat: 41.3851, lng: 2.1734 },
      
      // Andalucía
      'sevilla': { lat: 37.3891, lng: -5.9845 },
      'malaga': { lat: 36.7213, lng: -4.4214 },
      'granada': { lat: 37.1773, lng: -3.5986 },
      
      // País Vasco
      'bilbao': { lat: 43.2627, lng: -2.9253 },
      
      // Galicia
      'vigo': { lat: 42.2406, lng: -8.7207 },
      'coruña': { lat: 43.3623, lng: -8.4115 },
      
      // Otras ciudades importantes
      'zaragoza': { lat: 41.6488, lng: -0.8891 },
      'murcia': { lat: 37.9922, lng: -1.1307 },
      'palma': { lat: 39.5696, lng: 2.6502 }
    };
    
    if (!ciudad) return null;

    const ciudadNormalizada = ciudad.toLowerCase().trim();

    // Intentar primero con la ciudad tal como está
    if (ciudadesEspana[ciudadNormalizada]) {
      return ciudadesEspana[ciudadNormalizada];
    }

    // Intentar sin acentos
    const ciudadSinAcentos = ciudadNormalizada
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    return ciudadesEspana[ciudadSinAcentos] || null;
  }

  /**
   * Obtiene la sede más cercana basada en la localidad del usuario
   * @param {string} ciudad - Ciudad del usuario
   * @param {number} radioMaximo - Radio máximo de búsqueda en km
   * @returns {Promise<Object|null>} Sede más cercana con información adicional
   */
  async getSedeMasCercana(ciudad, radioMaximo = 100) {
    try {
      // Obtener coordenadas de la ciudad del usuario
      const coordenadasUsuario = this.getCoordenadasCiudad(ciudad);

      if (!coordenadasUsuario) {
        console.warn(`No se encontraron coordenadas para la ciudad: ${ciudad}`);
        return null;
      }

      // Buscar sedes cercanas
      const response = await this.getSedesCercanas(
        coordenadasUsuario.lat,
        coordenadasUsuario.lng,
        radioMaximo
      );

      if (!response.success || !response.data || response.data.length === 0) {
        return null;
      }

      // La primera sede es la más cercana (ya están ordenadas por distancia)
      const sedeMasCercana = response.data[0];

      return {
        sede: sedeMasCercana,
        distancia_km: sedeMasCercana.distancia_km,
        distancia_formateada: this.formatearDistancia(sedeMasCercana.distancia_km),
        coordenadas_usuario: coordenadasUsuario,
        ciudad_usuario: ciudad,
        total_sedes_cercanas: response.data.length
      };
    } catch (error) {
      console.error('Error al obtener sede más cercana:', error);
      return null;
    }
  }

  /**
   * Formatea la distancia para mostrar al usuario
   * @param {number} distanciaKm - Distancia en kilómetros
   * @returns {string} Distancia formateada
   */
  formatearDistancia(distanciaKm) {
    if (distanciaKm < 1) {
      return `${Math.round(distanciaKm * 1000)} m`;
    } else if (distanciaKm < 10) {
      return `${distanciaKm.toFixed(1)} km`;
    } else {
      return `${Math.round(distanciaKm)} km`;
    }
  }

  /**
   * Obtiene información de sedes cercanas para mostrar en el perfil
   * @param {string} ciudad - Ciudad del usuario
   * @returns {Promise<Object>} Información para mostrar en badges
   */
  async getInfoSedesCercanas(ciudad) {
    try {
      const sedeMasCercana = await this.getSedeMasCercana(ciudad);

      if (!sedeMasCercana) {
        return {
          tiene_sede_cercana: false,
          mensaje: 'No hay sedes cercanas disponibles',
          sede_cercana: null
        };
      }

      return {
        tiene_sede_cercana: true,
        sede_cercana: {
          id: sedeMasCercana.sede.id,
          nombre: sedeMasCercana.sede.nombre,
          poblacion: sedeMasCercana.sede.poblacion,
          distancia: sedeMasCercana.distancia_formateada,
          distancia_km: sedeMasCercana.distancia_km,
          campos_f8: sedeMasCercana.sede.num_campos_f8 || 0,
          campos_f11: sedeMasCercana.sede.num_campos_f11 || 0,
          contacto: sedeMasCercana.sede.contacto
        },
        total_sedes_cercanas: sedeMasCercana.total_sedes_cercanas,
        ciudad_usuario: ciudad
      };
    } catch (error) {
      console.error('Error al obtener info de sedes cercanas:', error);
      return {
        tiene_sede_cercana: false,
        mensaje: 'Error al buscar sedes cercanas',
        sede_cercana: null
      };
    }
  }

  /**
   * Obtiene sedes disponibles para un torneo específico
   * @param {number} torneoId - ID del torneo
   * @returns {Promise<Object>} Respuesta con sedes del torneo
   */
  async getSedesPorTorneo(torneoId) {
    try {
      const response = await api.get(`/postgres/torneos/${torneoId}/sedes`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener sedes del torneo:', error);
      throw error;
    }
  }

  /**
   * Obtiene información completa de sedes cercanas incluyendo torneos activos
   * @param {string} ciudad - Ciudad del usuario
   * @returns {Promise<Object>} Información completa para mostrar en badges
   */
  async getInfoCompletaSedesCercanas(ciudad) {
    try {
      const infoSedes = await this.getInfoSedesCercanas(ciudad);

      if (!infoSedes.tiene_sede_cercana) {
        return {
          ...infoSedes,
          torneos_info: {
            tiene_torneos_activos: false,
            torneos_activos: { count: 0, data: [] },
            proxima_jornada: null
          }
        };
      }

      // Obtener información de torneos para la sede más cercana
      const torneosInfo = await torneoService.getInfoTorneosParaBadges(infoSedes.sede_cercana.id);

      return {
        ...infoSedes,
        torneos_info: torneosInfo
      };
    } catch (error) {
      console.error('Error al obtener info completa de sedes cercanas:', error);
      return {
        tiene_sede_cercana: false,
        mensaje: 'Error al buscar información completa',
        sede_cercana: null,
        torneos_info: {
          tiene_torneos_activos: false,
          torneos_activos: { count: 0, data: [] },
          proxima_jornada: null
        }
      };
    }
  }
}

export default new SedeService();
